import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, Search } from 'lucide-react';
import { Input, TableDateRangePicker } from '@shared/ui';
import type {
  WalletMonitoringGroup,
  WalletMonitoringToken,
  WalletRiskLevel,
} from '@shared/api/contracts';
import {
  DEFAULT_WALLET_FILTERS,
  getNetworkDotColor,
  mergeStringOptions,
  toggleBalanceSource,
  toggleCurrency,
  toggleGroupCode,
  toggleKind,
  toggleNetwork,
  toggleRisk,
  toggleStatus,
  toggleToken,
  WALLET_BALANCE_SOURCES,
  WALLET_KIND_ORDER,
  WALLET_RISK_OPTIONS,
  WALLET_STATUS_OPTIONS,
  WALLET_TOKEN_ORDER,
  type WalletAddressStatus,
  type WalletFilterCatalog,
  type WalletsMonitoringFiltersPatch,
} from '@entities/wallets';
import { cn } from '@shared/lib/utils';

interface WalletsFiltersToolbarProps {
  groups: WalletMonitoringGroup[];
  catalog?: WalletFilterCatalog;
  onChange: (patch: WalletsMonitoringFiltersPatch) => void;
  filters: import('@entities/wallets').WalletsMonitoringFilters;
}

const RISK_DOT: Record<WalletRiskLevel, string> = {
  low: 'bg-emerald-600',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  critical: 'bg-red-600',
  unknown: 'bg-gray-400',
};

const STATUS_DOT: Record<WalletAddressStatus, string> = {
  active: 'bg-emerald-600',
  disabled: 'bg-gray-400',
  maintenance: 'bg-yellow-500',
  blocked: 'bg-red-600',
  receive_only: 'bg-orange-500',
  withdraw_only: 'bg-blue-600',
  archived: 'bg-gray-700',
};

const chipClass = (active: boolean) =>
  cn(
    'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-fast ease-standard sm:min-h-0',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-border text-muted-foreground hover:bg-accent',
  );

export function WalletsFiltersToolbar({
  groups,
  filters,
  catalog,
  onChange,
}: WalletsFiltersToolbarProps) {
  const { t } = useTranslation('cabinet');

  const networkOptions = useMemo(
    () => mergeStringOptions(catalog?.networkCodes ?? [], filters.networkCodes),
    [catalog?.networkCodes, filters.networkCodes],
  );

  const currencyOptions = useMemo(
    () => mergeStringOptions(catalog?.currencyCodes ?? [], filters.currencyCodes),
    [catalog?.currencyCodes, filters.currencyCodes],
  );

  const tokenOptions = useMemo(
    () =>
      mergeStringOptions(
        WALLET_TOKEN_ORDER,
        filters.tokens,
      ) as WalletMonitoringToken[],
    [filters.tokens],
  );

  return (
    <div className="surface-card flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="w-full sm:min-w-[200px] sm:flex-1">
          <Input
            icon={<Search className="h-4 w-4" />}
            placeholder={t('wallets.filters.searchPlaceholder')}
            value={filters.search}
            onChange={(event) =>
              onChange((prev) => ({ ...prev, search: event.target.value }))
            }
          />
        </div>
        <TableDateRangePicker
          className="w-full sm:w-auto sm:min-w-[220px]"
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          placeholder={t('wallets.filters.period')}
          onChange={({ dateFrom, dateTo }) =>
            onChange((prev) => ({ ...prev, dateFrom, dateTo }))
          }
        />
        <button
          type="button"
          onClick={() => onChange(DEFAULT_WALLET_FILTERS)}
          className="inline-flex min-h-tap shrink-0 items-center gap-1.5 rounded-md px-3 text-sm text-muted-foreground transition-colors duration-fast ease-standard hover:bg-accent hover:text-accent-foreground sm:min-h-0 sm:h-9"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('wallets.filters.reset')}
        </button>
      </div>

      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
          <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('wallets.filters.groupLabel')}
          </span>
          {groups.map((group) => (
            <button
              key={group.code}
              type="button"
              className={chipClass(filters.groupCodes.includes(group.code))}
              onClick={() =>
                onChange((prev) => ({
                  ...prev,
                  groupCodes: toggleGroupCode(prev.groupCodes, group.code),
                }))
              }
            >
              {group.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('wallets.filters.kindLabel')}
        </span>
        {WALLET_KIND_ORDER.map((kind) => (
          <button
            key={kind}
            type="button"
            className={chipClass(filters.walletKinds.includes(kind))}
            onClick={() =>
              onChange((prev) => ({
                ...prev,
                walletKinds: toggleKind(prev.walletKinds, kind),
              }))
            }
          >
            {t(`wallets.kinds.${kind}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('wallets.filters.balanceSourceLabel')}
        </span>
        {WALLET_BALANCE_SOURCES.map((source) => (
          <button
            key={source}
            type="button"
            className={chipClass(filters.balanceSources.includes(source))}
            onClick={() =>
              onChange((prev) => ({
                ...prev,
                balanceSources: toggleBalanceSource(prev.balanceSources, source),
              }))
            }
          >
            {t(`wallets.balanceSource.${source}`)}
          </button>
        ))}
      </div>

      {networkOptions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
          <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('wallets.filters.networkLabel')}
          </span>
          {networkOptions.map((network) => (
            <button
              key={network}
              type="button"
              className={chipClass(filters.networkCodes.includes(network))}
              onClick={() =>
                onChange((prev) => ({
                  ...prev,
                  networkCodes: toggleNetwork(prev.networkCodes, network),
                }))
              }
            >
              <span
                className="inline-block size-2 rounded-full"
                style={{ backgroundColor: getNetworkDotColor(network) }}
              />
              {network}
            </button>
          ))}
        </div>
      )}

      {currencyOptions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
          <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('wallets.filters.currencyLabel')}
          </span>
          {currencyOptions.map((currency) => (
            <button
              key={currency}
              type="button"
              className={chipClass(filters.currencyCodes.includes(currency))}
              onClick={() =>
                onChange((prev) => ({
                  ...prev,
                  currencyCodes: toggleCurrency(prev.currencyCodes, currency),
                }))
              }
            >
              {currency}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('wallets.filters.riskLabel')}
        </span>
        {WALLET_RISK_OPTIONS.map((risk) => (
          <button
            key={risk}
            type="button"
            className={chipClass(filters.riskLevels.includes(risk))}
            onClick={() =>
              onChange((prev) => ({
                ...prev,
                riskLevels: toggleRisk(prev.riskLevels, risk),
              }))
            }
          >
            <span className={`inline-block size-2 rounded-full ${RISK_DOT[risk]}`} />
            {t(`wallets.risk.${risk}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('wallets.filters.statusLabel')}
        </span>
        {WALLET_STATUS_OPTIONS.map((status) => (
          <button
            key={status}
            type="button"
            className={chipClass(filters.statuses.includes(status))}
            onClick={() =>
              onChange((prev) => ({
                ...prev,
                statuses: toggleStatus(prev.statuses, status),
              }))
            }
          >
            <span className={`inline-block size-2 rounded-full ${STATUS_DOT[status]}`} />
            {t(`wallets.status.${status}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('wallets.filters.tokenLabel')}
        </span>
        {tokenOptions.map((token) => (
          <button
            key={token}
            type="button"
            className={chipClass(filters.tokens.includes(token))}
            onClick={() =>
              onChange((prev) => ({
                ...prev,
                tokens: toggleToken(prev.tokens, token),
              }))
            }
          >
            {token}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-border/70 pt-3">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('wallets.filters.amlLabel')}
        </span>
        <button
          type="button"
          className={chipClass(filters.amlMonitoring === true)}
          onClick={() =>
            onChange((prev) => ({
              ...prev,
              amlMonitoring: prev.amlMonitoring === true ? null : true,
            }))
          }
        >
          {t('wallets.filters.amlEnabled')}
        </button>
        <button
          type="button"
          className={chipClass(filters.amlMonitoring === false)}
          onClick={() =>
            onChange((prev) => ({
              ...prev,
              amlMonitoring: prev.amlMonitoring === false ? null : false,
            }))
          }
        >
          {t('wallets.filters.amlDisabled')}
        </button>
      </div>
    </div>
  );
}
